import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  createFilterOptions,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { listProducts } from "../api/products";
import { listProductTypes } from "../api/productTypes";
import QuickProductDialog from "./QuickProductDialog";

// Slot-placeable product type codes per spec §11
const SLOT_PLACEABLE_CODES = ["ACTIVITY", "TRIP", "TUITION", "TRANSPORT"];

function indexBy(rows, key) {
  const m = {};
  rows.forEach((r) => { m[r[key]] = r; });
  return m;
}

const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function makeDayLabel(i, nights, startDate, arrivalDayOfWeek) {
  // dayIndex is 1-based and matches startDate (or arrivalDayOfWeek) on day 1.
  // Real startDate wins; fall back to the placeholder DOW when only that is set.
  let datePart = "";
  if (startDate) {
    datePart = ` · ${dayjs(startDate).add(i - 1, "day").format("ddd D MMM")}`;
  } else if (arrivalDayOfWeek != null && arrivalDayOfWeek >= 0) {
    datePart = ` · ${DOW_SHORT[(arrivalDayOfWeek + i - 1) % 7]}`;
  }
  if (i === 1) return `Day 1 — Arrival${datePart}`;
  if (i === nights + 1) return `Day ${i} — Departure${datePart}`;
  return `Day ${i}${datePart}`;
}

function findSlot(slots, dayIndex, slot) {
  return slots.find((s) => s.dayIndex === dayIndex && s.slot === slot);
}
function dayHas(slots, dayIndex, ...want) {
  return want.some((slot) => slots.some((s) => s.dayIndex === dayIndex && s.slot === slot));
}

export default function DayGridEditor({
  daySlots = [],
  nights = 0,
  disabled = false,
  locationId,
  seasonId,
  startDate,
  arrivalDayOfWeek,
  onChange,
  onCatalogueChanged,
}) {
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [dialog, setDialog] = useState(null); // { dayIndex, slot, name, productId? } while the trip/activity dialog is open

  const reloadProducts = useCallback(() => Promise.all([
    listProductTypes().catch(() => []),
    listProducts({
      activeOnly: true,
      locationId: locationId || undefined,
      seasonId: seasonId || undefined,
      date: startDate || undefined,
    }).catch(() => []),
  ])
    .then(([types, prods]) => {
      setProductTypes(Array.isArray(types) ? types : []);
      setProducts(Array.isArray(prods) ? prods : []);
    })
    .catch((e) => setLoadError(e.message || "Failed to load products")), [locationId, seasonId, startDate]);

  useEffect(() => { reloadProducts(); }, [reloadProducts]);

  const productById = useMemo(() => indexBy(products, "id"), [products]);
  const slotPlaceableTypeIds = useMemo(
    () => new Set(productTypes.filter((t) => SLOT_PLACEABLE_CODES.includes(t.code)).map((t) => t.id)),
    [productTypes],
  );
  const slotPlaceableProducts = useMemo(
    () => products.filter(
      (p) => slotPlaceableTypeIds.has(p.productTypeId) && p.hasAvailablePricing !== false,
    ),
    [products, slotPlaceableTypeIds],
  );

  const productsForSlot = (slot) => slotPlaceableProducts.filter((p) => {
    // Backend exposes eligibleSlots: string[] derived from this product's available
    // pricing rows in the quote's location/season/date context. Values: "AM_PM" / "FULL_DAY" /
    // "EVENING". When absent, default permissive — show everywhere — until backend ships the field.
    if (!p.eligibleSlots || p.eligibleSlots.length === 0) return true;
    if (slot === "FULL_DAY") return p.eligibleSlots.includes("FULL_DAY");
    // Evening cells use an EVENING price when there is one, otherwise the AM_PM price.
    if (slot === "EVE") return p.eligibleSlots.includes("EVENING") || p.eligibleSlots.includes("AM_PM");
    return p.eligibleSlots.includes("AM_PM");
  });

  const dayCount = Math.max(0, Number(nights || 0) + 1);
  const days = Array.from({ length: dayCount }, (_, i) => i + 1);

  const replaceSlot = (next) => {
    if (disabled) return;
    onChange?.(next);
  };

  const toggleFullDay = (dayIndex, asFullDay) => {
    if (disabled) return;
    if (asFullDay) {
      // Switching TO FULL_DAY: clear any AM/PM for this day (the evening stays)
      const hadAmOrPm = dayHas(daySlots, dayIndex, "AM", "PM");
      if (hadAmOrPm && !window.confirm("Switching to FULL_DAY will clear AM/PM for this day. Continue?")) return;
      const filtered = daySlots.filter((s) => !(s.dayIndex === dayIndex && (s.slot === "AM" || s.slot === "PM")));
      replaceSlot([...filtered, { dayIndex, slot: "FULL_DAY", productId: null, notes: null }]);
    } else {
      const hadFull = dayHas(daySlots, dayIndex, "FULL_DAY");
      if (hadFull && !window.confirm("Switching to AM/PM will clear FULL_DAY for this day. Continue?")) return;
      const filtered = daySlots.filter((s) => !(s.dayIndex === dayIndex && s.slot === "FULL_DAY"));
      replaceSlot(filtered);
    }
  };

  const setCell = (dayIndex, slot, partial) => {
    if (disabled) return;
    const existing = findSlot(daySlots, dayIndex, slot);
    let next;
    if (existing) {
      next = daySlots.map((s) =>
        s.dayIndex === dayIndex && s.slot === slot ? { ...s, ...partial } : s
      );
    } else {
      next = [...daySlots, { dayIndex, slot, productId: null, notes: null, ...partial }];
    }
    replaceSlot(next);
  };

  const removeCell = (dayIndex, slot) => {
    if (disabled) return;
    replaceSlot(daySlots.filter((s) => !(s.dayIndex === dayIndex && s.slot === slot)));
  };

  const placeProduct = (target, product) => {
    const cell = findSlot(daySlots, target.dayIndex, target.slot);
    setCell(target.dayIndex, target.slot, cell && !cell.productId ? { productId: product.id, notes: null } : { productId: product.id });
  };

  const onDialogSaved = async (product) => {
    const target = dialog;
    setDialog(null);
    await reloadProducts();
    if (!target) return;
    if (target.productId) onCatalogueChanged?.(); // edited: reprice the quote
    else placeProduct(target, product);
  };

  const renderCell = (dayIndex, slot) => (
    <SlotCell
      cell={findSlot(daySlots, dayIndex, slot)}
      productById={productById}
      options={productsForSlot(slot)}
      disabled={disabled}
      // Free-text cells keep their name in notes; picking a product replaces it.
      onPickProduct={(id) => placeProduct({ dayIndex, slot }, { id })}
      onFreeText={(text) => setCell(dayIndex, slot, { productId: null, notes: text })}
      onNotes={(text) => setCell(dayIndex, slot, { notes: text })}
      onClear={() => removeCell(dayIndex, slot)}
      onCreate={(name) => setDialog({ dayIndex, slot, name })}
      onEdit={(productId) => setDialog({ dayIndex, slot, productId })}
    />
  );

  if (dayCount === 0) {
    return <Typography color="text.secondary">Set nights to populate the day grid.</Typography>;
  }

  return (
    <Box>
      {loadError && <Typography color="error" sx={{ mb: 1 }}>{loadError}</Typography>}
      <Box sx={{ ...GRID, display: { xs: "none", md: "grid" }, px: 1.5, mb: 0.5 }}>
        <span />
        {["Morning", "Afternoon", "Evening"].map((h) => (
          <Typography key={h} variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>{h}</Typography>
        ))}
      </Box>
      <Stack spacing={1}>
        {days.map((dayIndex) => {
          const isFullDay = dayHas(daySlots, dayIndex, "FULL_DAY");
          return (
            <Paper key={dayIndex} variant="outlined" sx={{ ...GRID, p: 1.5, alignItems: "start" }}>
              <Box>
                <Typography variant="subtitle2">{makeDayLabel(dayIndex, nights, startDate, arrivalDayOfWeek)}</Typography>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isFullDay}
                      onChange={(e) => toggleFullDay(dayIndex, e.target.checked)}
                      disabled={disabled}
                    />
                  }
                  label={<Typography variant="body2">Full day</Typography>}
                />
              </Box>
              {isFullDay ? (
                <Box sx={{ gridColumn: { md: "span 2" } }}>
                  <CellLabel>Full day</CellLabel>
                  {renderCell(dayIndex, "FULL_DAY")}
                </Box>
              ) : (
                <>
                  <Box><CellLabel>Morning</CellLabel>{renderCell(dayIndex, "AM")}</Box>
                  <Box><CellLabel>Afternoon</CellLabel>{renderCell(dayIndex, "PM")}</Box>
                </>
              )}
              <Box><CellLabel>Evening</CellLabel>{renderCell(dayIndex, "EVE")}</Box>
            </Paper>
          );
        })}
      </Stack>
      <QuickProductDialog
        open={!!dialog}
        productId={dialog?.productId}
        initialName={dialog?.name || ""}
        cellSlot={dialog?.slot}
        productTypes={productTypes}
        existingProducts={slotPlaceableProducts}
        locationId={locationId}
        seasonId={seasonId}
        startDate={startDate}
        onClose={() => setDialog(null)}
        onSaved={onDialogSaved}
        onUseExisting={(product) => { const target = dialog; setDialog(null); if (target) placeProduct(target, product); }}
      />
    </Box>
  );
}

// Day label column, then three equal columns that may shrink (minmax(0, 1fr))
// so the evening cell never pushes past the card.
const GRID = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "140px repeat(3, minmax(0, 1fr))" },
  columnGap: 2,
  rowGap: 1,
};

// Per-cell slot label, only on narrow screens where the column header is hidden.
function CellLabel({ children }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "block", md: "none" } }}>
      {children}
    </Typography>
  );
}

const CREATE = { __create: true };
const filter = createFilterOptions();

/**
 * One day-grid cell. Holds a catalogue product (priced) or a free-text item
 * with no product (e.g. "Disco"; no cost). A product cell can carry a note.
 */
function SlotCell({ cell, productById, options, disabled, onPickProduct, onFreeText, onNotes, onClear, onCreate, onEdit }) {
  const product = cell?.productId ? productById[cell.productId] || { id: cell.productId, name: `#${cell.productId}` } : null;
  const freeText = !product && cell?.notes ? cell.notes : "";
  const [input, setInput] = useState(product ? product.name : freeText);
  const [notesOpen, setNotesOpen] = useState(false);
  const creatingRef = useRef(false); // the create dialog steals focus; don't save the typed text on that blur
  useEffect(() => { setInput(product ? product.name : freeText); }, [product?.id, product?.name, freeText]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitText = (raw) => {
    const text = (raw || "").trim();
    if (text === (product ? product.name : freeText)) return;
    if (!text) { onClear(); return; }
    const match = options.find((p) => p.name.toLowerCase() === text.toLowerCase());
    if (match) onPickProduct(match.id);
    else onFreeText(text);
  };

  const showNotes = !!product && (notesOpen || !!cell?.notes);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Autocomplete
          size="small"
          fullWidth
          freeSolo
          selectOnFocus
          handleHomeEndKeys
          disabled={disabled}
          options={options}
          value={product || freeText || null}
          inputValue={input}
          onInputChange={(_e, v, reason) => { if (reason !== "reset" || v) setInput(v); }}
          getOptionLabel={(o) => (typeof o === "string" ? o : o?.name || "")}
          isOptionEqualToValue={(o, v) => typeof v !== "string" && o?.id === v?.id}
          filterOptions={(opts, state) => {
            const out = filter(opts, state);
            return disabled ? out : [...out, CREATE];
          }}
          renderOption={(props, o) => (
            <li {...props} key={o.__create ? "__create" : o.id}>
              {o.__create
                ? <Typography color="primary" variant="body2">+ New trip / activity{input.trim() && !product ? ` "${input.trim()}"` : "…"}</Typography>
                : o.name}
            </li>
          )}
          onChange={(_e, val) => {
            if (val && val.__create) {
              creatingRef.current = true;
              onCreate(product ? "" : input.trim());
              return;
            }
            if (val == null) { setInput(""); onClear(); return; }
            if (typeof val === "string") { commitText(val); return; }
            onPickProduct(val.id);
          }}
          onBlur={() => {
            if (creatingRef.current) { creatingRef.current = false; setInput(product ? product.name : freeText); return; }
            commitText(input);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Pick or type…"
              helperText={freeText ? "No cost" : undefined}
              FormHelperTextProps={{ sx: { mx: 0 } }}
            />
          )}
        />
      </Stack>
      {product && !disabled && (
        <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }}>
          <Link component="button" type="button" variant="caption" underline="hover" onClick={() => onEdit(product.id)}>
            Edit
          </Link>
          <Link component="button" type="button" variant="caption" underline="hover" onClick={() => setNotesOpen(!showNotes)}>
            {cell?.notes ? (showNotes ? "Hide note" : "Note") : (showNotes ? "Hide note" : "Add note")}
          </Link>
        </Stack>
      )}
      {showNotes && (
        <TextField
          size="small"
          placeholder="Note"
          multiline
          minRows={1}
          maxRows={4}
          fullWidth
          sx={{ mt: 1 }}
          disabled={disabled}
          value={cell?.notes || ""}
          onChange={(e) => onNotes(e.target.value || null)}
        />
      )}
    </Box>
  );
}
